import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  MdBookmarkBorder,
  MdChevronLeft,
  MdChevronRight,
  MdClose,
  MdCloudUpload,
  MdContentCopy,
  MdDeleteOutline,
  MdSettings,
  MdViewModule,
} from 'react-icons/md';
import admissionsAPI from '../../api/admissionsAPI';
import OdooAutocomplete from './OdooAutocomplete';
import { useEmployeeAttendanceAuth } from '../../context/EmployeeAttendanceAuthContext';
import { useEmployeeAttendanceHrAuth } from '../../context/EmployeeAttendanceHrAuthContext';
import { fetchWithHrToken, HR_SESSION_ERROR } from '../../utils/hrApiHelpers';
import '../../employeeAttendance.css';
import '../../employeeAttendance-hr.css';
import '../../employeeAttendance-admissions.css';

const EMPTY_FORM = {
  first_name: '',
  middle_name: '',
  last_name: '',
  gender: '',
  phone: '',
  email: '',
  faculty_id: '',
  department_id: '',
  visit_date: new Date().toISOString().slice(0, 10),
  visit_purpose: 'information',
  source_of_information: '',
  other_source: '',
  campus: '',
  admission_intake_id: '',
  is_admitted: false,
  visited_person: '',
  school_id: '',
  district: '',
  notes: '',
};

const CAMPUS_FALLBACK = [
  { value: 'prof_addow', label: 'Prof Addow Campus' },
  { value: 'prof_shahiid', label: 'Prof Shahiid Campus' },
  { value: 'prof_shurie', label: 'Prof Shurie Campus' },
];

function odooId(value) {
  if (value == null || value === false) return '';
  if (Array.isArray(value)) return String(value[0] ?? '');
  if (typeof value === 'object' && value.id != null) return String(value.id);
  return String(value);
}

function firstId(...values) {
  for (const v of values) {
    const id = odooId(v);
    if (id && id !== 'false') return id;
  }
  return '';
}

function firstText(...values) {
  for (const v of values) {
    if (v == null || v === false || v === '') continue;
    if (Array.isArray(v)) return String(v[1] ?? v[0] ?? '');
    if (typeof v === 'object' && v.name != null) return String(v.name);
    return String(v);
  }
  return '';
}

function Field({ label, children, required }) {
  return (
    <div className="ea-odoo-field">
      <label className="ea-odoo-field__label">
        {label}
        {required ? <span className="ea-adm-req">*</span> : null}
      </label>
      <div className="ea-odoo-field__value">{children}</div>
    </div>
  );
}

function RadioRow({ name, value, onChange, options, required }) {
  return (
    <div className="ea-adm-radios" role="radiogroup">
      {options.map((opt, idx) => (
        <label key={opt.value} className="ea-adm-radio">
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            required={required && idx === 0 && !value}
          />
          <span className="ea-adm-radio__label">{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

function Group({ title, children }) {
  return (
    <section className="ea-odoo-group">
      <h3 className="ea-odoo-group__title">{title}</h3>
      <div className="ea-odoo-group__body">{children}</div>
    </section>
  );
}

export default function ProspectiveStudentFormPage() {
  const { prospectiveId } = useParams();
  const isNew = !prospectiveId || prospectiveId === 'new';
  const navigate = useNavigate();
  const { ensureHrToken } = useEmployeeAttendanceHrAuth();
  const { user } = useEmployeeAttendanceAuth();

  const [form, setForm] = useState(EMPTY_FORM);
  const [campuses, setCampuses] = useState(CAMPUS_FALLBACK);
  const [faculties, setFaculties] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [schools, setSchools] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const actionsRef = useRef(null);

  const setField = (key, value) => {
    setDirty(true);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const fullName = useMemo(
    () => [form.first_name, form.middle_name, form.last_name].filter(Boolean).join(' '),
    [form.first_name, form.middle_name, form.last_name]
  );

  const titleName = isNew ? 'New' : fullName || 'Prospective student';
  const showDirtyActions = isNew || dirty;

  useEffect(() => {
    if (!actionsOpen) return undefined;
    const onDoc = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) {
        setActionsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [actionsOpen]);

  const loadLookups = useCallback(async () => {
    const jobs = [
      ['campuses', (t) => admissionsAPI.getCampuses(t)],
      ['faculties', (t) => admissionsAPI.getFaculties(t)],
      ['schools', (t) => admissionsAPI.getSchools('', t, { limit: 500 })],
      ['intakes', (t) => admissionsAPI.getIntakes(t)],
      ['districts', (t) => admissionsAPI.getDistricts(t)],
      ['purposes', (t) => admissionsAPI.getVisitPurposes(t)],
      ['sources', (t) => admissionsAPI.getInfoSources(t)],
    ];
    for (const [key, fn] of jobs) {
      const { data } = await fetchWithHrToken(ensureHrToken, fn);
      if (!data) continue;
      if (key === 'campuses') {
        const list = data.campuses || data.records || [];
        if (list.length) setCampuses(list);
      }
      if (key === 'faculties') setFaculties(data.faculties || data.records || []);
      if (key === 'schools') setSchools(data.schools || data.records || []);
      if (key === 'intakes') setIntakes(data.intakes || data.records || []);
      if (key === 'districts') setDistricts(data.districts || data.records || []);
      if (key === 'purposes') setPurposes(data.records || []);
      if (key === 'sources') setSources(data.records || []);
    }
  }, [ensureHrToken]);

  const loadDepartments = useCallback(
    async (facultyId) => {
      if (!facultyId) {
        setDepartments([]);
        return;
      }
      const { data } = await fetchWithHrToken(ensureHrToken, (token) =>
        admissionsAPI.getDepartments(facultyId, token)
      );
      setDepartments(data?.departments || data?.records || []);
    },
    [ensureHrToken]
  );

  const mapToForm = (p) => {
    const faculty_id = firstId(p.faculty_id_int, p.faculty_id, p.faculty);
    const department_id = firstId(p.department_id_int, p.department_id, p.department);
    setForm({
      ...EMPTY_FORM,
      first_name: firstText(p.first_name),
      middle_name: firstText(p.middle_name),
      last_name: firstText(p.last_name),
      gender: firstText(p.gender),
      phone: firstText(p.phone),
      email: firstText(p.email),
      faculty_id,
      department_id,
      visit_date: firstText(p.visit_date).slice(0, 10) || EMPTY_FORM.visit_date,
      visit_purpose: firstText(p.visit_purpose) || 'information',
      source_of_information: firstText(p.source_of_information),
      other_source: firstText(p.other_source),
      campus: firstText(p.campus),
      admission_intake_id: firstId(p.admission_intake_id_int, p.admission_intake_id, p.admission_intake),
      is_admitted: Boolean(p.is_admitted),
      visited_person: firstText(p.visited_person),
      school_id: firstId(p.school_id_int, p.school_id, p.school),
      district: firstText(p.district),
      notes: firstText(p.notes),
    });
  };

  const loadRecord = useCallback(async () => {
    if (isNew) {
      setForm({ ...EMPTY_FORM });
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.getProspectiveStudent({ prospective_id: Number(prospectiveId) }, token)
    );
    if (fetchError === 'no_token') {
      setError(user?.hr_api_error || HR_SESSION_ERROR);
      setLoading(false);
      return;
    }
    if (fetchError) {
      setError(fetchError);
      setLoading(false);
      return;
    }
    mapToForm(data.prospective || data.record || data);
    setDirty(false);
    setLoading(false);
  }, [ensureHrToken, isNew, prospectiveId, user?.hr_api_error]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  useEffect(() => {
    loadDepartments(form.faculty_id);
  }, [form.faculty_id, loadDepartments]);

  const buildPayload = () => {
    const payload = {
      first_name: form.first_name.trim(),
      middle_name: form.middle_name.trim(),
      last_name: form.last_name.trim(),
      gender: form.gender,
      phone: form.phone.trim(),
      email: form.email.trim() || false,
      faculty_id: Number(form.faculty_id),
      department_id: Number(form.department_id),
      visit_date: form.visit_date,
      visit_purpose: form.visit_purpose,
      source_of_information: form.source_of_information,
      campus: form.campus,
      admission_intake_id: Number(form.admission_intake_id),
      is_admitted: Boolean(form.is_admitted),
      visited_person: form.visited_person,
      school_id: Number(form.school_id),
      district: form.district,
      notes: form.notes.trim() || false,
    };
    if (form.source_of_information === 'other') {
      payload.other_source = form.other_source.trim();
    }
    return payload;
  };

  const onDiscard = () => {
    if (isNew) {
      navigate('/bu-dashboard/admissions/prospective');
      return;
    }
    loadRecord();
  };

  const onDuplicate = async () => {
    if (isNew || actionBusy) return;
    setActionsOpen(false);
    setActionBusy(true);
    setError(null);
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.duplicateProspectiveStudent(Number(prospectiveId), token)
    );
    setActionBusy(false);
    if (fetchError) {
      setError(fetchError);
      return;
    }
    const newId = data?.prospective?.id;
    if (newId) {
      navigate(`/bu-dashboard/admissions/prospective/${newId}`, { replace: true });
    }
  };

  const openDeleteModal = () => {
    if (isNew || actionBusy) return;
    setActionsOpen(false);
    setDeleteOpen(true);
  };

  const onDeleteConfirm = async () => {
    if (isNew || actionBusy) return;
    setActionBusy(true);
    setError(null);
    const { error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.deleteProspectiveStudent(Number(prospectiveId), token)
    );
    setActionBusy(false);
    if (fetchError) {
      setError(fetchError);
      setDeleteOpen(false);
      return;
    }
    setDeleteOpen(false);
    navigate('/bu-dashboard/admissions/prospective', { replace: true });
  };

  const onSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    setError(null);
    const payload = buildPayload();
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      isNew
        ? admissionsAPI.createProspectiveStudent(payload, token)
        : admissionsAPI.updateProspectiveStudent(
            { prospective_id: Number(prospectiveId), ...payload },
            token
          )
    );
    setSaving(false);
    if (fetchError) {
      setError(fetchError);
      return;
    }
    setDirty(false);
    const createdId = data?.prospective?.id;
    if (isNew && createdId) {
      navigate(`/bu-dashboard/admissions/prospective/${createdId}`, { replace: true });
      return;
    }
    await loadRecord();
  };

  if (loading) {
    return (
      <div className="ea-odoo-form-page ea-adm-form ea-prospective-form">
        <p className="ea-hr-table__empty">Loading…</p>
      </div>
    );
  }

  return (
    <div className="ea-odoo-form-page ea-adm-form ea-prospective-form">
      <div className="ea-odoo-form-bar ea-adm-cp">
        <div className="ea-adm-cp__left">
          <button
            type="button"
            className="ea-odoo-btn ea-adm-cp__new"
            onClick={() => navigate('/bu-dashboard/admissions/prospective/new')}
          >
            New
          </button>

          <div className="ea-adm-cp__title-block">
            <Link
              to="/bu-dashboard/admissions/prospective"
              className="ea-adm-cp__parent"
            >
              Prospective Students
            </Link>
            {!isNew && (
              <div className="ea-adm-cp__record-row">
                <span className="ea-adm-cp__record">{titleName}</span>
                <div className="ea-adm-actions" ref={actionsRef}>
                  <button
                    type="button"
                    className="ea-adm-cp__icon"
                    title="Actions"
                    aria-label="Actions"
                    aria-expanded={actionsOpen}
                    aria-haspopup="menu"
                    onClick={() => setActionsOpen((v) => !v)}
                    disabled={actionBusy}
                  >
                    <MdSettings size={16} />
                  </button>
                  {actionsOpen ? (
                    <div className="ea-adm-actions__menu" role="menu">
                      <button
                        type="button"
                        className="ea-adm-actions__item"
                        role="menuitem"
                        onClick={onDuplicate}
                        disabled={actionBusy}
                      >
                        <MdContentCopy size={16} aria-hidden />
                        Duplicate
                      </button>
                      <button
                        type="button"
                        className="ea-adm-actions__item ea-adm-actions__item--danger"
                        role="menuitem"
                        onClick={openDeleteModal}
                        disabled={actionBusy}
                      >
                        <MdDeleteOutline size={16} aria-hidden />
                        Delete
                      </button>
                    </div>
                  ) : null}
                </div>
                {showDirtyActions && (
                  <>
                    <button
                      type="button"
                      className="ea-adm-cp__icon ea-adm-cp__icon--save"
                      onClick={onSave}
                      disabled={saving || actionBusy}
                      title="Save"
                      aria-label="Save"
                    >
                      <MdCloudUpload size={18} />
                    </button>
                    <button
                      type="button"
                      className="ea-adm-cp__icon"
                      onClick={onDiscard}
                      disabled={saving || actionBusy}
                      title="Discard"
                      aria-label="Discard"
                    >
                      <MdClose size={18} />
                    </button>
                  </>
                )}
              </div>
            )}
            {isNew && (
              <div className="ea-adm-cp__record-row">
                <button
                  type="button"
                  className="ea-adm-cp__icon"
                  title="Actions"
                  aria-label="Actions"
                  disabled
                >
                  <MdSettings size={16} />
                </button>
                <button
                  type="button"
                  className="ea-adm-cp__icon ea-adm-cp__icon--save"
                  onClick={onSave}
                  disabled={saving}
                  title="Save"
                  aria-label="Save"
                >
                  <MdCloudUpload size={18} />
                </button>
                <button
                  type="button"
                  className="ea-adm-cp__icon"
                  onClick={onDiscard}
                  disabled={saving}
                  title="Discard"
                  aria-label="Discard"
                >
                  <MdClose size={18} />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="ea-adm-cp__right">
          <button
            type="button"
            className="ea-adm-cp__icon"
            title="Favorite"
            aria-label="Favorite"
          >
            <MdBookmarkBorder size={18} />
          </button>
          {isNew ? (
            <button
              type="button"
              className="ea-adm-cp__icon"
              title="Views"
              aria-label="Views"
              onClick={() => navigate('/bu-dashboard/admissions/prospective')}
            >
              <MdViewModule size={18} />
            </button>
          ) : (
            <div className="ea-odoo-pager ea-adm-cp__pager" aria-label="Pagination">
              <span>1 / 1</span>
              <div className="ea-adm-cp__pager-btns">
                <button
                  type="button"
                  className="ea-odoo-pager__btn"
                  onClick={() => navigate('/bu-dashboard/admissions/prospective')}
                  aria-label="Previous"
                >
                  <MdChevronLeft size={18} />
                </button>
                <button type="button" className="ea-odoo-pager__btn" disabled aria-label="Next">
                  <MdChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="ea-odoo-alert ea-odoo-alert--error" role="alert">
          {error}
        </div>
      )}

      <form className="ea-odoo-sheet" onSubmit={onSave}>
        <header className="ea-odoo-sheet__header">
          <input
            className="ea-prospective-form__name"
            value={fullName}
            readOnly
            placeholder="Full Name"
            aria-label="Full Name"
          />
        </header>

        <div className="ea-odoo-sheet__body">
          <div className="ea-odoo-cols">
            <Group title="Personal Information">
              <Field label="First Name" required>
                <input
                  value={form.first_name}
                  onChange={(e) => setField('first_name', e.target.value)}
                  required
                />
              </Field>
              <Field label="Middle Name" required>
                <input
                  value={form.middle_name}
                  onChange={(e) => setField('middle_name', e.target.value)}
                  required
                />
              </Field>
              <Field label="Last Name" required>
                <input
                  value={form.last_name}
                  onChange={(e) => setField('last_name', e.target.value)}
                  required
                />
              </Field>
              <Field label="Gender" required>
                <RadioRow
                  name="gender"
                  value={form.gender}
                  onChange={(v) => setField('gender', v)}
                  required
                  options={[
                    { value: 'male', label: 'Male' },
                    { value: 'female', label: 'Female' },
                  ]}
                />
              </Field>
            </Group>

            <Group title="Contact Information">
              <Field label="Phone Number" required>
                <input
                  value={form.phone}
                  onChange={(e) => setField('phone', e.target.value)}
                  required
                />
              </Field>
              <Field label="Email Address">
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                />
              </Field>
            </Group>
          </div>

          <div className="ea-odoo-cols">
            <Group title="Academic Interest">
              <Field label="Faculty of Interest" required>
                <OdooAutocomplete
                  value={form.faculty_id}
                  options={faculties}
                  required
                  aria-label="Faculty of Interest"
                  onChange={(id) => {
                    setField('faculty_id', id);
                    setField('department_id', '');
                  }}
                />
              </Field>
              <Field label="Department of Interest" required>
                <OdooAutocomplete
                  value={form.department_id}
                  options={departments}
                  onChange={(id) => setField('department_id', id)}
                  required
                  aria-label="Department of Interest"
                />
              </Field>
            </Group>

            <Group title="Visit Information">
              <Field label="Visit Date" required>
                <input
                  type="date"
                  value={form.visit_date}
                  onChange={(e) => setField('visit_date', e.target.value)}
                  required
                />
              </Field>
              <Field label="Purpose of Visit" required>
                <OdooAutocomplete
                  value={form.visit_purpose}
                  options={purposes}
                  onChange={(id) => setField('visit_purpose', id)}
                  required
                  aria-label="Purpose of Visit"
                />
              </Field>
              <Field label="How did you hear about us?" required>
                <OdooAutocomplete
                  value={form.source_of_information}
                  options={sources}
                  onChange={(id) => setField('source_of_information', id)}
                  required
                  aria-label="How did you hear about us?"
                />
              </Field>
              {form.source_of_information === 'other' ? (
                <Field label="Other Source" required>
                  <input
                    value={form.other_source}
                    onChange={(e) => setField('other_source', e.target.value)}
                    required
                  />
                </Field>
              ) : null}
            </Group>
          </div>

          <div className="ea-odoo-cols">
            <Group title="Campus & Admission">
              <Field label="Campus" required>
                <OdooAutocomplete
                  value={form.campus}
                  options={campuses}
                  onChange={(id) => setField('campus', id)}
                  required
                  aria-label="Campus"
                />
              </Field>
              <Field label="Admission Intake" required>
                <OdooAutocomplete
                  value={form.admission_intake_id}
                  options={intakes}
                  onChange={(id) => setField('admission_intake_id', id)}
                  required
                  aria-label="Admission Intake"
                />
              </Field>
              <Field label="Is he/she admitted" required>
                <RadioRow
                  name="is_admitted"
                  value={form.is_admitted ? 'yes' : 'no'}
                  onChange={(v) => setField('is_admitted', v === 'yes')}
                  required
                  options={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                  ]}
                />
              </Field>
              <Field label="Visited Person" required>
                <RadioRow
                  name="visited_person"
                  value={form.visited_person}
                  onChange={(v) => setField('visited_person', v)}
                  required
                  options={[
                    { value: 'student', label: 'Student' },
                    { value: 'parent', label: 'Parent' },
                  ]}
                />
              </Field>
            </Group>

            <Group title="School & Location">
              <Field label="School Name" required>
                <OdooAutocomplete
                  value={form.school_id}
                  options={schools}
                  onChange={(id) => setField('school_id', id)}
                  required
                  aria-label="School Name"
                />
              </Field>
              <Field label="District" required>
                <OdooAutocomplete
                  value={form.district}
                  options={districts}
                  onChange={(id) => setField('district', id)}
                  required
                  aria-label="District"
                />
              </Field>
            </Group>
          </div>

          <Group title="Additional Information">
            <Field label="Notes">
              <textarea
                rows={4}
                value={form.notes}
                onChange={(e) => setField('notes', e.target.value)}
                placeholder="Any additional notes about this prospective student..."
              />
            </Field>
          </Group>
        </div>

        <div className="ea-adm-form-actions">
          <button type="submit" className="ea-odoo-btn" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="ea-odoo-btn ea-odoo-btn--ghost" onClick={onDiscard}>
            Discard
          </button>
        </div>
      </form>

      {deleteOpen ? (
        <div
          className="ea-odoo-modal-backdrop"
          role="presentation"
          onClick={() => !actionBusy && setDeleteOpen(false)}
        >
          <div
            className="ea-odoo-modal ea-odoo-modal--delete"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ea-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ea-odoo-modal__header">
              <h2 id="ea-delete-title">Bye-bye, record!</h2>
              <button
                type="button"
                className="ea-odoo-modal__close"
                aria-label="Close"
                disabled={actionBusy}
                onClick={() => setDeleteOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="ea-odoo-modal__body">
              <p>Ready to make your record disappear into thin air? Are you sure?</p>
              <p>It will be gone forever!</p>
              <p>Think twice before you click that &apos;Delete&apos; button!</p>
            </div>
            <div className="ea-odoo-modal__footer">
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--danger"
                disabled={actionBusy}
                onClick={onDeleteConfirm}
              >
                {actionBusy ? 'Deleting…' : 'Delete'}
              </button>
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--ghost"
                disabled={actionBusy}
                onClick={() => setDeleteOpen(false)}
              >
                No, keep it
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
